<template>
  <v-row data-tourid="rev-view-team-members">
    <v-col cols="12" v-if="hasInactiveUsers">
      <p class="amber--text caption align-center mt-3 pa-0 mb-0">
        <v-tooltip top max-width="300">
          <template v-slot:activator="{ on }">
            <v-icon id="inactive_user_tooltip" v-on="on" color="amber" small>
              mdi-information-outline
            </v-icon>
          </template>
          <p>{{ $t('common.inactive_users') }}</p>
          <p>
            {{ $t('validation.create_rev.you_have_selected_an_inactive_user') }}
          </p>
        </v-tooltip>
        {{ $t('common.inactive_team_members') }}
      </p>
    </v-col>

    <v-col cols="12" class="mt-3 mb-6 d-flex align-end flex-wrap">
      <div class="d-flex flex-column align-end">
        <AssigneesSearchPopover
          :disabled="!canEdit"
          :allowEmptySelection="false"
          :selectedAccountIds="ownerId"
          :blacklist="ownerBlacklist"
          :zIndex="1"
          @change="ownerSelected"
        >
          <TeamMember
            v-if="isOwnerAssigned"
            :account="owner"
            :userId="owner._id"
            :organizationId="organizationId"
            :name="owner.name"
            :role="$t('common.roles.owner')"
            :avatarMediaObject="owner.photo"
            :inactive="owner.status !== 'joined'"
          />
        </AssigneesSearchPopover>
      </div>
      <div class="d-flex flex-column align-end">
        <AssigneesSearchPopover
          :allowEmptySelection="false"
          :disabled="!canEdit"
          :selectedAccountIds="coachId"
          :blacklist="coachBlacklist"
          :filters="{ isCoach: true }"
          :zIndex="1"
          @change="coachSelected"
        >
          <TeamMember
            v-if="isCoachAssigned"
            :account="coach"
            :userId="coach._id"
            :organizationId="organizationId"
            :name="coach.name"
            :role="$t('common.roles.coach_one')"
            :avatarMediaObject="coach.photo"
            :inactive="coach.status !== 'joined'"
          />
          <div
            v-else-if="canEdit"
            data-testid="rev-team-add-coach"
            style="padding-bottom: 2px"
            class="d-flex align-center me-3 cursor-pointer"
          >
            <v-icon color="grey">
              mdi-plus
            </v-icon>
            <p class="ma-0 pa-0 white--text caption">
              {{ $t('rev.create.add_coach') }}
            </p>
          </div>
        </AssigneesSearchPopover>
      </div>
      <AssigneesSearchPopover
        multiple
        :maxSelectableAccounts="5"
        :disabled="!canEdit"
        :selectedAccountIds="collaboratorsIds"
        :blacklist="collaboratorsBlacklist"
        :zIndex="1"
        @change="collaboratorsSelected"
      >
        <div class="d-flex align-end flex-wrap">
          <template v-if="collaborators.length > 0">
            <TeamMember
              v-for="(collaborator, index) in collaborators"
              :class="collaborators.length >= 3 ? 'mt-2' : ''"
              :account="collaborator"
              :userId="collaborator._id"
              :organizationId="organizationId"
              :key="collaborator.id"
              :role="index === 0 ? $t('common.roles.collaborator_other') : ''"
              :name="collaborator.shortenedName"
              :avatarMediaObject="collaborator.photo"
              :inactive="collaborator.status !== 'joined'"
              :editable="canEdit"
              @click:close="handleRemoveCollaborator(index)"
            />
          </template>

          <div
            v-if="canEdit && collaborators.length < 5"
            style="padding-bottom: 2px"
            class="d-flex align-center cursor-pointer mt-2"
            data-testid="rev-team-add-member"
          >
            <v-icon color="grey">
              mdi-plus
            </v-icon>
            <p class="ma-0 pa-0 caption white--text">
              {{ $t('rev.create.add_collaborators_members') }}
            </p>
          </div>
        </div>
      </AssigneesSearchPopover>
    </v-col>
  </v-row>
</template>

<script>
import _ from 'lodash'

import addIcon from '@/assets/rev/addIcon.svg'

import mixin from '@/mixins/mixin.vue'

import TeamMember from '@/components/Rev/TeamMember'
import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'

export default {
  name: 'RevTeam',

  mixins: [mixin],

  components: {
    TeamMember,
    AssigneesSearchPopover,
  },

  data() {
    return {
      addIcon,
      currentCollaborators: [],
    }
  },
  props: {
    rev: {
      type: Object,
      required: true,
    },
    availableCoaches: {
      type: Array,
      required: false,
    },
    availableOwners: {
      type: Array,
      required: false,
    },
    availableCollaborators: {
      type: Array,
      required: false,
    },
    organizationId: {
      type: String,
      required: true,
    },
    siteId: {
      type: String,
      required: false,
    },
    canEdit: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    owner() {
      return this.rev?.team?.owner ?? {}
    },

    ownerBlacklist() {
      return _.compact([
        this.coach?._id,
        ...(this.collaborators?.map(collaborator => collaborator._id) ?? []),
      ])
    },

    coach() {
      return this.rev?.team?.coach ?? {}
    },

    coachId() {
      return this.coach?._id ? [this.coach?._id] : []
    },

    ownerId() {
      return this.owner?._id ? [this.owner?._id] : []
    },

    coachBlacklist() {
      return _.compact([
        this.owner?._id,
        ...(this.collaborators?.map(collaborator => collaborator._id) ?? []),
      ])
    },

    collaborators() {
      return this.rev.team.collaborators
    },

    collaboratorsIds() {
      return this.currentCollaborators.length
        ? this.currentCollaborators
        : this.collaborators?.map(collaborator => collaborator._id)
    },

    collaboratorsBlacklist() {
      return _.compact([this.owner?._id, this.coach?._id])
    },

    isCoachAssigned() {
      return !_.isEmpty(this.coach)
    },
    isOwnerAssigned() {
      return !_.isEmpty(this.owner)
    },
    coachOptions() {
      const selectedCollaboratorIds = this.collaborators.map(c => c?.id)

      return this.availableCoaches.filter(
        coach =>
          !selectedCollaboratorIds.includes(coach?.id) &&
          coach.id !== this.owner.id
      )
    },
    ownerOptions() {
      const selectedCollaboratorIds = this.collaborators.map(c => c?.id)
      return this.availableOwners.filter(
        owner =>
          !selectedCollaboratorIds.includes(owner?.id) &&
          owner.id !== this.coach.id
      )
    },

    collaboratorOptions() {
      return this.availableCollaborators?.filter(
        user => user.id !== this.coach.id && user.id !== this.owner.id
      )
    },

    hasInactiveUsers() {
      let inactiveCollaborators = []
      if (!_.isEmpty(this.collaborators)) {
        inactiveCollaborators = this.collaborators.filter(
          collaborator => collaborator.status !== 'joined'
        )
      }
      return Boolean(
        inactiveCollaborators.length ||
          _.get(this.coach, 'status', 'joined') !== 'joined'
      )
    },
  },

  methods: {
    ownerSelected(selectedUser) {
      this.$emit('ownerSelected', selectedUser.items?.[0])
    },
    coachSelected(selectedUser) {
      this.$emit('coachSelected', selectedUser.items?.[0] ?? null)
    },

    collaboratorsSelected(selectedCollaborators) {
      this.currentCollaborators = selectedCollaborators?.ids ?? []
      this.$emit('collaboratorsSelected', selectedCollaborators.items)
    },

    handleRemoveCollaborator(index) {
      const newCollaborators = this.collaborators.filter(
        (collaborator, i) => i !== index
      )
      this.currentCollaborators = newCollaborators.map(
        newCollaborator => newCollaborator._id
      )
      this.$emit('collaboratorsSelected', newCollaborators)
    },
  },
}
</script>

<style lang="scss">
#rev_view {
  .popover {
    color: #fff;
    border: 1px solid $color-blue;
    border-left: 1px solid $color-blue;
    background-color: $color-blue;
    border-radius: 4px;
    font-size: 12px;

    .arrow:after {
      border-right-color: $color-blue;
    }
    .popover-title {
      padding: 8px 14px 0px 14px;
    }
    .popover-title,
    .popover-content {
      background-color: $color-blue;
      border-bottom: 1px solid $color-blue;
    }
  }
}
</style>
